import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { ELLIPSIS_SX } from './utils';

const COPIED_LABEL_MS = 1500;

interface CreativeIdLineProps {
  creativeId: string;
  /** For a card's dark thumbnail overlay — white text, white/70 icon that brightens on hover. */
  onDark?: boolean;
  /** Show only a copy icon, with "Copy ID" as its tooltip, without the id itself. */
  idHidden?: boolean;
}

/** Creative ID + copy-to-clipboard button, shared by every table cell and grid card that shows one. */
export function CreativeIdLine({
  creativeId,
  onDark = false,
  idHidden = false,
}: CreativeIdLineProps): ReactNode {
  const [copied, setCopied] = useState(false);

  function handleCopy(): void {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(creativeId)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_LABEL_MS);
      })
      .catch(() => {});
  }

  if (idHidden) {
    return (
      <Tooltip title={copied ? 'Copied' : 'Copy ID'} placement="top">
        <IconButton
          size="small"
          aria-label="Copy ID"
          onClick={(event) => {
            event.stopPropagation();
            handleCopy();
          }}
          sx={{ flexShrink: 0 }}
        >
          <ContentCopyOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Tooltip title={creativeId} placement="top">
        <Typography
          variant="h5"
          sx={{
            ...ELLIPSIS_SX,
            minWidth: 0,
            flex: onDark ? undefined : '1 1 auto',
            color: onDark ? 'common.white' : undefined,
          }}
        >
          {creativeId}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleCopy();
          }}
          sx={
            onDark
              ? (theme) => ({
                  flexShrink: 0,
                  color: alpha(theme.palette.common.white, 0.7),
                  '&:hover': { color: 'common.white' },
                })
              : undefined
          }
        >
          <ContentCopyOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
